import { RoleName } from '@/common/const/Auth';
import { isProduction } from '@/common/environment';
import {
  EXPERIMENTS,
  getTenant,
  getTenantSeqNumber,
  GRAB,
} from '@/common/Tenants';
import {
  ServiceItem,
  TechFamily,
  TechFamilyItem,
  TFRole,
  UserRoleScopeMapping,
} from '@/lib/App';
import {
  EXPERIMENT_PUBLISHER_POLICY,
  experimentPublisherActions,
  normalizeLegacyRoleName,
  normalizePolicyName,
  ROLLOUT_CRITICAL_PUBLISHER_POLICY,
  ROLLOUT_NON_CRITICAL_PUBLISHER_POLICY,
  rolloutPublisherActions,
  UserAction,
  UserActionConfig,
} from '@/utils/accessControl';
import {
  getPermissionConfig,
  RoleNameToValue,
} from '@/utils/accessControl/config';

export const ACCESS_CONTROL_PAGE_SIZE = 15;
export const ACCESS_CONTROL_OPTION_LIMIT = 20;
export const ADMIN_SCOPE_LABEL = 'All services & tech families';
export const GRAB_ACCESS_REQUEST_TENANT_SEQ = 1;

export const ServicePolicyName = {
  ROLLOUT_CRITICAL_PUBLISHER: ROLLOUT_CRITICAL_PUBLISHER_POLICY,
  ROLLOUT_NON_CRITICAL_PUBLISHER: ROLLOUT_NON_CRITICAL_PUBLISHER_POLICY,
  EXPERIMENT_PUBLISHER: EXPERIMENT_PUBLISHER_POLICY,
} as const;

export const ACCESS_REQUEST_ENVIRONMENTS = {
  STAGING: 'staging',
  PRODUCTION: 'production',
} as const;

export type AccessRequestEnvironment =
  (typeof ACCESS_REQUEST_ENVIRONMENTS)[keyof typeof ACCESS_REQUEST_ENVIRONMENTS];

export const ACCESS_REQUEST_BASE_URL_BY_ENVIRONMENT: Record<
  AccessRequestEnvironment,
  string
> = {
  [ACCESS_REQUEST_ENVIRONMENTS.STAGING]:
    'https://console.stg-myteksi.com/concedo',
  [ACCESS_REQUEST_ENVIRONMENTS.PRODUCTION]: 'https://concedo.grab.com',
};

export const GRAB_ACCESS_REQUEST_APPLICATION_IDS: Record<
  AccessRequestEnvironment,
  string
> = {
  [ACCESS_REQUEST_ENVIRONMENTS.STAGING]: 'dd9975adc1a04f83b73326768fbd4049',
  [ACCESS_REQUEST_ENVIRONMENTS.PRODUCTION]: '2f2b605ac80e4595b8623027b6302f23',
};

export const ACCESS_REQUEST_APPLICATION_IDS_BY_TENANT: Record<
  string,
  Record<AccessRequestEnvironment, string>
> = {
  [EXPERIMENTS]: GRAB_ACCESS_REQUEST_APPLICATION_IDS,
  [GRAB]: GRAB_ACCESS_REQUEST_APPLICATION_IDS,
};

export type AccessScopeType = 'service' | 'tech-family';

export type AccessScopeOption = {
  id: string;
  label: string;
  type: AccessScopeType;
};

export type AccessControlRow = {
  key: string;
  serviceOrTechFamily: string;
  rolloutCriticalPublisher: boolean;
  rolloutNonCriticalPublisher: boolean;
  experimentPublisher: boolean;
  editor: boolean;
  publisher: boolean;
  admin: boolean;
  accessRequest?: boolean;
};

export type PermissionMatrixObjectType = 'variable' | 'rollout' | 'experiment';

export type PermissionMatrixTab = {
  key: PermissionMatrixObjectType;
  label: string;
};

export type PermissionMatrixRow = {
  key: string;
  action: UserAction;
  actionLabel: string;
  allowed: boolean;
};

type UserRoleWithOptionalRole = UserRoleScopeMapping & {
  role?: string;
  policy_names?: string[];
};

const ActionByObjectType: Record<PermissionMatrixObjectType, UserAction[]> = {
  variable: [
    UserAction.CREATE_A_VARIABLE,
    UserAction.CREATE_VARIABLE_USING_CURRENT_TF,
    UserAction.EDIT_CURRENT_VARIABLE,
    UserAction.EDIT_CURRENT_VARIABLE_USING_CURRENT_TF,
    UserAction.ARCHIVE_CURRENT_VARIABLE,
    UserAction.UNDO_ARCHIVE_CURRENT_VARIABLE,
  ],
  rollout: [
    UserAction.CREATE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
    UserAction.APPROVE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
    UserAction.REJECT_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
  ],
  experiment: [
    UserAction.CREATE_AN_EXPERIMENT,
    UserAction.CLONE_CURRENT_EXPERIMENT,
    UserAction.EDIT_CURRENT_EXPERIMENT,
    UserAction.CREATE_EXPERIMENT_USING_CURRENT_VARIABLE,
    UserAction.EDIT_EXPERIMENT_USING_CURRENT_VARIABLE,
    UserAction.ABORT_CURRENT_EXPERIMENT,
    UserAction.PAUSE_CURRENT_EXPERIMENT,
    UserAction.APPROVE_CURRENT_EXPERIMENT,
    UserAction.REJECT_CURRENT_EXPERIMENT,
  ],
};

const serviceDefaultActions = new Set<UserAction>([
  UserAction.CREATE_A_VARIABLE,
  UserAction.CREATE_AN_EXPERIMENT,
  UserAction.CLONE_CURRENT_EXPERIMENT,
  UserAction.EDIT_CURRENT_EXPERIMENT,
  UserAction.CREATE_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
  UserAction.REJECT_ROLLOUT_DIFF_FOR_CURRENT_VARIABLE,
  UserAction.CREATE_GROUP,
]);

const createEmptyRow = (serviceOrTechFamily: string): AccessControlRow => ({
  key: serviceOrTechFamily,
  serviceOrTechFamily,
  rolloutCriticalPublisher: false,
  rolloutNonCriticalPublisher: false,
  experimentPublisher: false,
  editor: false,
  publisher: false,
  admin: false,
});

const getPolicyNames = (role: UserRoleWithOptionalRole): string[] => {
  if (Array.isArray(role?.policyNames)) {
    return role.policyNames.map(normalizePolicyName).filter(Boolean);
  }

  if (Array.isArray(role?.policy_names)) {
    return role.policy_names.map(normalizePolicyName).filter(Boolean);
  }

  return [];
};

const getRoleName = (role: UserRoleWithOptionalRole): string =>
  normalizeLegacyRoleName(role?.role ?? role?.roleName, {
    techFamily: role?.tf,
    policyNames: getPolicyNames(role),
  });

const getRowLabel = (role: UserRoleWithOptionalRole): string =>
  (role?.service ?? '').trim() || (role?.tf ?? '').trim();

const applyRoleToRow = (
  row: AccessControlRow,
  roleName: string,
  policyNames: string[],
): AccessControlRow => ({
  ...row,
  rolloutCriticalPublisher:
    row.rolloutCriticalPublisher ||
    policyNames.includes(ServicePolicyName.ROLLOUT_CRITICAL_PUBLISHER),
  rolloutNonCriticalPublisher:
    row.rolloutNonCriticalPublisher ||
    policyNames.includes(ServicePolicyName.ROLLOUT_NON_CRITICAL_PUBLISHER),
  experimentPublisher:
    row.experimentPublisher ||
    policyNames.includes(ServicePolicyName.EXPERIMENT_PUBLISHER),
  editor: row.editor || roleName === RoleName.EDITOR,
  publisher: row.publisher || roleName === RoleName.PUBLISHER,
  admin: row.admin || roleName === RoleName.ADMIN,
});

export const buildAccessControlRows = (
  userRoles: UserRoleScopeMapping[] | null | undefined,
  userTFRoles: TFRole[] | null | undefined,
  isAdmin: boolean,
): AccessControlRow[] => {
  if (isAdmin) {
    return [
      {
        ...createEmptyRow(ADMIN_SCOPE_LABEL),
        admin: true,
      },
    ];
  }

  const rowsByLabel = new Map<string, AccessControlRow>();

  (Array.isArray(userRoles) ? userRoles : []).forEach((role) => {
    const rowLabel = getRowLabel(role);

    if (!rowLabel) return;

    const row = rowsByLabel.get(rowLabel) ?? createEmptyRow(rowLabel);
    rowsByLabel.set(
      rowLabel,
      applyRoleToRow(row, getRoleName(role), getPolicyNames(role)),
    );
  });

  (Array.isArray(userTFRoles) ? userTFRoles : []).forEach((role) => {
    const rowLabel = (role?.tf ?? '').trim();

    if (!rowLabel) return;

    const row = rowsByLabel.get(rowLabel) ?? createEmptyRow(rowLabel);
    rowsByLabel.set(rowLabel, applyRoleToRow(row, getRoleName(role), []));
  });

  return Array.from(rowsByLabel.values());
};

export const filterAccessControlRows = (
  rows: AccessControlRow[],
  selectedScope: string,
): AccessControlRow[] => {
  const normalizedScope = selectedScope.trim();

  if (!normalizedScope) return rows;

  return rows.filter((row) => row.serviceOrTechFamily === normalizedScope);
};

export const buildAccessRequestRow = (
  selectedScope: string,
): AccessControlRow => {
  const serviceOrTechFamily = selectedScope.trim();

  return {
    ...createEmptyRow(serviceOrTechFamily),
    key: `${serviceOrTechFamily}-access-request`,
    accessRequest: true,
  };
};

export const hasAccessInVisibleColumns = (
  row: AccessControlRow,
  enableServiceAuthZ: boolean,
): boolean =>
  (enableServiceAuthZ &&
    (row.rolloutCriticalPublisher ||
      row.rolloutNonCriticalPublisher ||
      row.experimentPublisher)) ||
  row.editor ||
  row.publisher ||
  row.admin;

const getAccessRequestEnvironment = (
  isProductionEnvironment: boolean,
): AccessRequestEnvironment =>
  isProductionEnvironment
    ? ACCESS_REQUEST_ENVIRONMENTS.PRODUCTION
    : ACCESS_REQUEST_ENVIRONMENTS.STAGING;

export const getAccessRequestApplicationId = ({
  tenant = getTenant(),
  tenantSeqNumber = getTenantSeqNumber(),
  isProductionEnvironment = isProduction,
}: {
  tenant?: string;
  tenantSeqNumber?: number;
  isProductionEnvironment?: boolean;
} = {}): string | undefined => {
  const environment = getAccessRequestEnvironment(isProductionEnvironment);
  const applicationIds =
    ACCESS_REQUEST_APPLICATION_IDS_BY_TENANT[tenant] ??
    (tenantSeqNumber === GRAB_ACCESS_REQUEST_TENANT_SEQ
      ? GRAB_ACCESS_REQUEST_APPLICATION_IDS
      : undefined);

  return applicationIds?.[environment];
};

export const shouldShowAccessRequestRow = ({
  selectedScope,
  filteredRows,
  isAdmin,
  enableServiceAuthZ,
}: {
  selectedScope: string;
  filteredRows: AccessControlRow[];
  isAdmin: boolean;
  enableServiceAuthZ: boolean;
}): boolean => {
  if (!selectedScope.trim() || isAdmin) return false;

  return !filteredRows.some((row) =>
    hasAccessInVisibleColumns(row, enableServiceAuthZ),
  );
};

export const getAccessRequestUrl = ({
  tenant = getTenant(),
  tenantSeqNumber = getTenantSeqNumber(),
  isProductionEnvironment = isProduction,
}: {
  tenant?: string;
  tenantSeqNumber?: number;
  isProductionEnvironment?: boolean;
} = {}): string | undefined => {
  const environment = getAccessRequestEnvironment(isProductionEnvironment);
  const applicationId = getAccessRequestApplicationId({
    tenant,
    tenantSeqNumber,
    isProductionEnvironment,
  });

  if (!applicationId) return undefined;

  return `${ACCESS_REQUEST_BASE_URL_BY_ENVIRONMENT[environment]}/application/${applicationId}/roles`;
};

const getTechFamilyOptionLabel = (
  techFamily: TechFamily | TechFamilyItem | string | null | undefined,
): string => {
  if (!techFamily) return '';
  if (typeof techFamily === 'string') return techFamily.trim();

  if (typeof techFamily === 'object' && 'prefix' in techFamily)
    return (techFamily.prefix ?? '').trim();

  return (typeof techFamily === 'object' ? techFamily.name : '').trim();
};

export const buildAccessScopeOptions = (
  services: ServiceItem[] | null | undefined,
  techFamilies:
    | Array<TechFamily | TechFamilyItem | string | null | undefined>
    | null
    | undefined,
): AccessScopeOption[] => {
  const seenServiceLabels = new Set<string>();
  const serviceOptions = (Array.isArray(services) ? services : [])
    .map((service) => (service?.name ?? '').trim())
    .filter(Boolean)
    .filter((label) => {
      if (seenServiceLabels.has(label)) return false;

      seenServiceLabels.add(label);
      return true;
    })
    .map((label) => ({
      id: label,
      label,
      type: 'service' as const,
    }));
  const seenTechFamilyLabels = new Set<string>();
  const techFamilyOptions = (Array.isArray(techFamilies) ? techFamilies : [])
    .map(getTechFamilyOptionLabel)
    .filter(Boolean)
    .filter((label) => {
      if (seenServiceLabels.has(label) || seenTechFamilyLabels.has(label)) {
        return false;
      }

      seenTechFamilyLabels.add(label);
      return true;
    })
    .map((label) => ({
      id: label,
      label,
      type: 'tech-family' as const,
    }));
  const options: AccessScopeOption[] = [];

  const pushOption = (option?: AccessScopeOption) => {
    if (!option) return;

    options.push(option);
  };

  for (
    let index = 0;
    options.length < ACCESS_CONTROL_OPTION_LIMIT &&
    (index < serviceOptions.length || index < techFamilyOptions.length);
    index += 1
  ) {
    pushOption(serviceOptions[index]);
    pushOption(techFamilyOptions[index]);
  }

  return options.slice(0, ACCESS_CONTROL_OPTION_LIMIT);
};

export const getPermissionMatrixTabs = (): PermissionMatrixTab[] => [
  {
    key: 'variable',
    label: 'Variable',
  },
  {
    key: 'rollout',
    label: 'Rollout',
  },
  {
    key: 'experiment',
    label: 'Experiment',
  },
];

export const getAccessControlActionsByObjectType = (
  objectType: PermissionMatrixObjectType,
): UserAction[] => ActionByObjectType[objectType] ?? [];

export const getAccessControlActionLabel = (action: UserAction): string =>
  UserActionConfig[action]?.text ?? UserAction[action];

const canLegacyRolePerformAction = (
  roles: string[],
  action: UserAction,
): boolean => {
  const requiredRole = getPermissionConfig(action).role;
  const requiredRoleValue = RoleNameToValue[requiredRole] ?? Number.MAX_VALUE;

  return roles.some((roleName) => {
    const userRoleValue = RoleNameToValue[roleName] ?? -1;

    return userRoleValue >= requiredRoleValue;
  });
};

const canServicePolicyPerformAction = (
  policyName: string,
  action: UserAction,
): boolean => {
  if (
    policyName === ServicePolicyName.ROLLOUT_CRITICAL_PUBLISHER ||
    policyName === ServicePolicyName.ROLLOUT_NON_CRITICAL_PUBLISHER
  ) {
    return rolloutPublisherActions.has(action);
  }

  if (policyName === ServicePolicyName.EXPERIMENT_PUBLISHER) {
    return experimentPublisherActions.has(action);
  }

  return false;
};

const canPerformMatrixAction = (
  userTFRoles: TFRole[] | null | undefined,
  userRoles: UserRoleScopeMapping[] | null | undefined,
  enableServiceAuthZ: boolean,
  action: UserAction,
): boolean => {
  const legacyRoleNames = (Array.isArray(userTFRoles) ? userTFRoles : []).map(
    (role) => getRoleName(role),
  );
  const concededRoles = Array.isArray(userRoles) ? userRoles : [];
  const concededRoleNames = concededRoles.map(getRoleName);
  const roleNames = [...legacyRoleNames, ...concededRoleNames];

  if (roleNames.includes(RoleName.ADMIN)) return true;

  if (canLegacyRolePerformAction(roleNames, action)) return true;

  if (!enableServiceAuthZ) return false;

  const serviceRoles = concededRoles.filter((role) =>
    (role?.service ?? '').trim(),
  );

  if (!serviceRoles.length) return false;

  if (serviceDefaultActions.has(action)) return true;

  const servicePolicyNames = Array.from(
    new Set(serviceRoles.flatMap(getPolicyNames).filter(Boolean)),
  );

  return servicePolicyNames.some((policyName) =>
    canServicePolicyPerformAction(policyName, action),
  );
};

export const buildPermissionMatrixRows = (
  tab: PermissionMatrixTab,
  userTFRoles: TFRole[] | null | undefined,
  userRoles: UserRoleScopeMapping[] | null | undefined,
  enableServiceAuthZ: boolean,
): PermissionMatrixRow[] =>
  getAccessControlActionsByObjectType(tab.key).map((action) => ({
    key: `${tab.key}-${action}`,
    action,
    actionLabel: getAccessControlActionLabel(action),
    allowed: canPerformMatrixAction(
      userTFRoles,
      userRoles,
      enableServiceAuthZ,
      action,
    ),
  }));
